import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { Check, Copy, Image, LayoutGrid, Minus, Package, Plus, Trash2, X } from 'lucide-react';
import {
  AdminPage,
  Button,
  Drawer,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
} from '@/design/components';
import { useModule } from '@/access/useModule';
import { usePerm } from '@/access/usePerm';
import { useEmployeeOptions } from '@/features/employees/api';
import { cn } from '@/lib/cn';
import { errorMessage } from '@/lib/errors';
import { useImageUrl } from '../api';
import { ProductPicker } from './ProductPicker';
import {
  usePlanogramBoards,
  usePlanogramMutations,
  type BranchPlanogram,
  type Planogram,
  type PlanogramShelf,
} from './planogramApi';
import { useZones, zonesFor, type Zone } from './zonesApi';
import { useProductCategories } from './api';

/**
 * פלנוגרמה (PRD 5.10, הרחבה).
 *
 * האיור הוא העניין: מדפים אחד מתחת לשני, ועל כל מדף המוצרים
 * ברוחב שהם באמת תופסים. טבלה היתה מראה את אותו מידע ולא היתה
 * עונה על השאלה שבגללה פותחים את המסך — "איך זה אמור להיראות".
 *
 * המדף העליון למעלה, כמו שמסתכלים על היחידה.
 */
export function PlanogramPage() {
  const canManage = usePerm('products.manage');
  /** הכניסה לויטרינה מופיעה רק למי שמנהל אותה, וכשהמודול דולק. */
  const canManageOps = usePerm('ops.manage');
  const hasDisplayModule = useModule('ops_display');
  const canDisplay = canManageOps && hasDisplayModule;
  const { data: options } = useEmployeeOptions();
  const branches = options?.branches ?? [];

  /** הסניפים המוצגים. ריק = כולם, וזה המצב שבו משווים. */
  const [branchIds, setBranchIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  const board = usePlanogramBoards(branchIds);
  const mutations = usePlanogramMutations();
  const zones = useZones();
  const departments = useProductCategories();

  /**
   * היחידות מקובצות לפי אזור, ובתוכו לפי מחלקה.
   *
   * השרת כבר החזיר אותן בסדר הזה, ולכן כאן רק חותכים. מי שאין
   * לו סיווג יורד לקבוצה אחרונה משלו במקום להתחבא באמצע.
   */
  const groups = useMemo(() => {
    const byKey = new Map<
      string,
      { area: string | null; department: string | null; units: BranchPlanogram[] }
    >();

    for (const unit of board.data ?? []) {
      const key = `${unit.area ?? ''}|${unit.department ?? ''}`;
      const group = byKey.get(key);
      if (group) group.units.push(unit);
      else byKey.set(key, { area: unit.area, department: unit.department, units: [unit] });
    }

    return [...byKey.values()];
  }, [board.data]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor),
  );

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: Promise<unknown>) => {
    setError(null);
    void action.catch((caught: unknown) => setError(errorMessage(caught)));
  };

  function onDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over) return;

    const slotId = String(active.id).replace('slot:', '');
    const shelfId = String(over.id).replace('shelf:', '');

    const shelves = (board.data ?? []).flatMap((unit) => unit.shelves);
    const from = shelves.find((shelf) => shelf.slots.some((slot) => slot.id === slotId));
    const to = shelves.find((shelf) => shelf.id === shelfId);
    if (!from || !to) return;

    // הסדר החדש של מדף היעד: מה שכבר שם, ועוד המוצר שהגיע
    const order = [...to.slots.filter((slot) => slot.id !== slotId).map((slot) => slot.id), slotId];
    if (from.id === to.id && order.join() === to.slots.map((slot) => slot.id).join()) return;

    run(mutations.moveSlot.mutateAsync({ slotId, shelfId, order }));
  }

  return (
    <AdminPage
      title="פלנוגרמה"
      subtitle="איך המדפים אמורים להיראות בכל סניף"
      backTo="/admin/products"
      actions={
        branches.length > 0 && (
          <div className="flex flex-wrap items-end gap-2">
            <Link to="/admin/products/planogram/zones" className="no-underline">
              <Button variant="secondary">
                <LayoutGrid size={18} strokeWidth={1.75} aria-hidden="true" />
                אזורים
              </Button>
            </Link>
            {/*
              ויטרינה יושבת כאן ולא בתפריט. היא ההוכחה שהמדף סודר
              לפי המפרט שבמסך הזה, ומי שבודק אותה מגיע מכאן.
            */}
            {canDisplay && (
              <Link to="/admin/ops/display" className="no-underline">
                <Button variant="secondary">
                  <Image size={18} strokeWidth={1.75} aria-hidden="true" />
                  ויטרינה
                </Button>
              </Link>
            )}
            <Button variant="primary" onClick={() => setIsAdding(true)}>
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              יחידת תצוגה
            </Button>
          </div>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {/* ריק = כל הסניפים, כדי להשוות ביניהם */}
      {branches.length > 1 && (
        <BranchPicker branches={branches} selected={branchIds} onChange={setBranchIds} />
      )}

      {isAdding && (
        <NewUnitPanel
          branches={branches}
          selected={branchIds}
          zones={zones.data ?? []}
          departments={departments.data ?? []}
          onCancel={() => setIsAdding(false)}
          onCreate={(input) =>
            run(
              mutations.createUnit.mutateAsync(input).then((created) => {
                setIsAdding(false);
                if (created === 0) {
                  setError('לא נוצרה יחידה חדשה. בסניפים שנבחרו כבר יש יחידה באותו שם.');
                }
              }),
            )
          }
        />
      )}

      {board.isPending && <Skeleton className="h-64" />}
      {board.isError && <ErrorState onRetry={() => void board.refetch()} />}

      {board.data?.length === 0 && !isAdding && (
        <EmptyState
          title="אין עדיין פלנוגרמה"
          description="יחידת תצוגה היא קיר או גונדולה. עליה מסמנים איפה כל מוצר עומד."
        />
      )}

      <DndContext sensors={sensors} onDragEnd={onDragEnd} accessibility={{ announcements }}>
        <div className="grid gap-6">
          {groups.map((group) => (
            <section key={`${group.area ?? ''}|${group.department ?? ''}`} className="grid gap-3">
              {/*
                כותרת הקבוצה אומרת איפה בחנות ומה נמכר שם. יחידה
                בלי סיווג אינה מוסתרת אלא מסומנת, כדי שמי שמסווג
                יראה בדיוק מה נשאר לו.
              */}
              <h2 className="m-0 flex flex-wrap items-baseline gap-2 text-title font-semibold text-ink">
                {group.area ?? 'בלי אזור'}
                {group.department && (
                  <span className="text-body font-normal text-muted">· {group.department}</span>
                )}
                {!group.department && (
                  <span className="text-body font-normal text-muted">· בלי מחלקה</span>
                )}
                <span className="text-body-sm font-normal text-muted">({group.units.length})</span>
              </h2>

              <div className="grid gap-4">
                {group.units.map((unit) => (
                  <Unit
                    key={unit.id}
                    unit={unit}
                    branches={branches.filter((branch) => branch.id !== unit.branch_id)}
                    zones={zones.data ?? []}
                    departments={departments.data ?? []}
                    showBranch={branchIds.length !== 1}
                    onError={setError}
                    run={run}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </DndContext>
    </AdminPage>
  );
}

/** בחירת הסניפים שמוצגים. ריק = כולם. */
function BranchPicker({
  branches,
  selected,
  onChange,
}: {
  branches: { id: string; name: string; color?: string | null }[];
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onChange([])}
        aria-pressed={selected.length === 0}
        className={cn(
          'min-h-11 cursor-pointer rounded-pill border px-3 text-body-sm font-medium transition-colors',
          selected.length === 0
            ? 'border-charcoal bg-charcoal text-on-charcoal'
            : 'border-muted bg-transparent text-ink hover:bg-cream',
        )}
      >
        כל הסניפים
      </button>

      {branches.map((branch) => {
        const isOn = selected.includes(branch.id);
        return (
          <button
            key={branch.id}
            type="button"
            aria-pressed={isOn}
            onClick={() =>
              onChange(isOn ? selected.filter((id) => id !== branch.id) : [...selected, branch.id])
            }
            className={cn(
              'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border px-3 text-body-sm transition-colors',
              isOn
                ? 'border-ink font-semibold text-ink'
                : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
              isOn && !branch.color && 'bg-olive',
            )}
            style={isOn && branch.color ? { backgroundColor: branch.color } : undefined}
          >
            {isOn && <Check size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />}
            {branch.name}
          </button>
        );
      })}
    </div>
  );
}

/**
 * יחידה חדשה, באיזה אזור, באיזו מחלקה ובאילו סניפים.
 *
 * ארבע השאלות יחד ולא רק שם: יחידה נבנית פעם אחת ויורדת לרשת,
 * והסיווג שנדחה ל"אחר כך" נשאר ריק. הסניפים מתחילים ממה שמוצג
 * כרגע, כי זה מה שעומד מול העיניים.
 */
function NewUnitPanel({
  branches,
  selected,
  zones,
  departments,
  onCreate,
  onCancel,
}: {
  branches: { id: string; name: string; color?: string | null }[];
  selected: string[];
  zones: Zone[];
  /** המחלקות של הקטלוג (מיגרציה 090). */
  departments: { id: string; name: string }[];
  onCreate: (input: {
    branchIds: string[];
    name: string;
    area_id: string | null;
    department_id: string | null;
  }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [areaId, setAreaId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [chosen, setChosen] = useState<string[]>(
    selected.length > 0 ? selected : branches.map((branch) => branch.id),
  );

  // רשתי בלבד: אזור של סניף אחד אינו חל על האחרים, ויצירה
  // משותפת לכמה סניפים אינה יכולה לשאת אותו
  const areas = zonesFor(zones, null);

  return (
    <Panel title="יחידת תצוגה חדשה">
      <form
        className="grid gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (name.trim() === '' || chosen.length === 0) return;
          onCreate({
            branchIds: chosen,
            name: name.trim(),
            area_id: areaId || null,
            department_id: departmentId || null,
          });
        }}
      >
        <div className="grid gap-3 sm:grid-cols-3">
          <label className="grid gap-1">
            <span className="text-label font-medium">שם היחידה</span>
            <input
              autoFocus
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="למשל: קיר הוויטמינים"
              className={UNIT_FIELD}
            />
          </label>

          <label className="grid gap-1">
            <span className="text-label font-medium">אזור</span>
            <select
              value={areaId}
              onChange={(event) => setAreaId(event.target.value)}
              className={UNIT_FIELD}
            >
              <option value="">בלי אזור</option>
              {areas.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </label>

          <label className="grid gap-1">
            <span className="text-label font-medium">מחלקה</span>
            <select
              value={departmentId}
              onChange={(event) => setDepartmentId(event.target.value)}
              className={UNIT_FIELD}
            >
              <option value="">בלי מחלקה</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="p-0 text-label font-medium">
            באילו סניפים <span className="font-normal text-muted">אפשר לבחור כמה</span>
          </legend>
          <BranchPicker
            branches={branches}
            selected={chosen.length === branches.length ? [] : chosen}
            onChange={(next) =>
              setChosen(next.length === 0 ? branches.map((branch) => branch.id) : next)
            }
          />
        </fieldset>

        <div className="flex flex-wrap gap-2">
          <Button type="submit" variant="primary" disabled={name.trim() === ''}>
            {chosen.length === 1 ? 'הוספה לסניף אחד' : `הוספה ל${chosen.length} סניפים`}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            ביטול
          </Button>
        </div>

        <p className="m-0 text-body-sm text-muted">
          כל יחידה נולדת עם שלושה מדפים ריקים, ואפשר להוסיף ולמחוק. סניף שכבר יש בו יחידה באותו שם
          ידולג. אזור או מחלקה של סניף מסוים נקבעים ביחידה עצמה, אחרי שהיא נוצרה.
        </p>
      </form>
    </Panel>
  );
}

const UNIT_FIELD =
  'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink placeholder:text-muted';

const announcements = {
  onDragStart: () => 'התחלת גרירה. חצים להזזה, רווח להפלה, Escape לביטול.',
  onDragOver: () => undefined,
  onDragEnd: ({ over }: { over: { id: string | number } | null }) =>
    over ? 'המוצר הועבר למדף.' : 'הגרירה הסתיימה מחוץ ליחידה, ושום דבר לא השתנה.',
  onDragCancel: () => 'הגרירה בוטלה.',
};

function Unit({
  unit,
  branches,
  zones,
  departments,
  showBranch,
  run,
}: {
  unit: BranchPlanogram;
  /** הסניפים האחרים, ליעד ההעתקה. */
  branches: { id: string; name: string; color?: string | null }[];
  zones: Zone[];
  /** המחלקות של הקטלוג (מיגרציה 090). */
  departments: { id: string; name: string }[];
  /** שם הסניף על הכרטיס. מיותר כשמוצג סניף אחד בלבד. */
  showBranch: boolean;
  onError: (message: string) => void;
  run: (action: Promise<unknown>) => void;
}) {
  const mutations = usePlanogramMutations();
  const [isCopying, setIsCopying] = useState(false);

  const slots = unit.shelves.flatMap((shelf) => shelf.slots);
  const facings = slots.reduce((sum, slot) => sum + slot.facings, 0);

  // הרשתיים ועוד אלה של הסניף הזה. אזור של סניף אחר אינו קיים
  // ליחידה הזו, והשרת ידחה אותו ממילא
  const areas = zonesFor(zones, unit.branch_id);

  const setZone = (field: 'area_id' | 'department_id', value: string) =>
    run(
      mutations.setZones.mutateAsync({
        id: unit.id,
        area_id: field === 'area_id' ? value || null : unit.area_id,
        department_id: field === 'department_id' ? value || null : unit.department_id,
      }),
    );

  return (
    <Panel
      title={unit.name}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          {showBranch && (
            <span
              className="inline-flex items-center gap-1.5 rounded-pill border border-line px-2 py-0.5 text-label font-medium text-ink"
              style={unit.branch_color ? { backgroundColor: unit.branch_color } : undefined}
            >
              {unit.branch}
            </span>
          )}

          {/*
            הסיווג נערך על הכרטיס ולא במסך נפרד: מי שמסתכל על
            היחידה הוא מי שיודע איפה היא עומדת.
          */}
          <label className="flex items-center gap-1">
            <span className="sr-only">אזור של {unit.name}</span>
            <select
              value={unit.area_id ?? ''}
              onChange={(event) => setZone('area_id', event.target.value)}
              className="min-h-11 rounded-sm border border-muted bg-surface px-2 text-body-sm text-ink"
            >
              <option value="">בלי אזור</option>
              {areas.map((zone) => (
                <option key={zone.id} value={zone.id}>
                  {zone.name}
                </option>
              ))}
            </select>
          </label>

          <label className="flex items-center gap-1">
            <span className="sr-only">מחלקה של {unit.name}</span>
            <select
              value={unit.department_id ?? ''}
              onChange={(event) => setZone('department_id', event.target.value)}
              className="min-h-11 rounded-sm border border-muted bg-surface px-2 text-body-sm text-ink"
            >
              <option value="">בלי מחלקה</option>
              {departments.map((department) => (
                <option key={department.id} value={department.id}>
                  {department.name}
                </option>
              ))}
            </select>
          </label>

          {branches.length > 0 && (
            <Button variant="secondary" onClick={() => setIsCopying(true)}>
              <Copy size={18} strokeWidth={1.75} aria-hidden="true" />
              העתקה לסניפים
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() =>
              run(
                mutations.addShelf.mutateAsync({
                  planogram_id: unit.id,
                  sort_order: unit.shelves.length,
                }),
              )
            }
          >
            <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
            מדף
          </Button>
          <button
            type="button"
            aria-label={`מחיקת ${unit.name}`}
            onClick={() => run(mutations.removeUnit.mutateAsync(unit.id))}
            className="grid size-11 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-cream hover:text-bad"
          >
            <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
          </button>
        </div>
      }
    >
      <p className="m-0 mb-3 text-body-sm text-muted">
        <span className="tabular">{unit.shelves.length}</span> מדפים ·{' '}
        <span className="tabular">{slots.length}</span> מוצרים ·{' '}
        <span className="tabular">{facings}</span> פנים. מספר הפנים הוא כמה יחידות מהמוצר עומדות זו
        לצד זו, והרוחב על המדף נגזר ממנו.
      </p>

      {unit.shelves.length === 0 ? (
        <p className="m-0 text-body-sm text-muted">אין מדפים ביחידה הזו.</p>
      ) : (
        /* היחידה כרהיט: עמודי צד. בלי המסגרת האיור נקרא כמו רשימת
           שורות, ובדיוק מזה רוצים להימלט — מי שמסדר עומד מול קיר,
           לא מול טבלה. */
        <div className="flex items-stretch overflow-hidden rounded-sm bg-cream">
          <span className="w-2 shrink-0 bg-charcoal" aria-hidden="true" />
          <div className="grid grow gap-3 px-2 py-2">
            {unit.shelves.map((shelf, index) => (
              <Shelf key={shelf.id} shelf={shelf} index={index} run={run} />
            ))}
          </div>
          <span className="w-2 shrink-0 bg-charcoal" aria-hidden="true" />
        </div>
      )}

      {isCopying && (
        <CopyDialog unit={unit} branches={branches} onClose={() => setIsCopying(false)} />
      )}
    </Panel>
  );
}

/**
 * העתקת היחידה לסניפים אחרים.
 *
 * העתקה ולא שיתוף: אחרי ההעתקה כל סניף עומד בפני עצמו, כי קיר
 * משותף שנערך במקום אחד היה משנה בשקט סניפים שאיש לא הסתכל
 * עליהם. זה נאמר כאן במסך ולא רק במיגרציה, אחרת המשתמש היה
 * מצפה לסינכרון שאינו קיים.
 */
function CopyDialog({
  unit,
  branches,
  onClose,
}: {
  unit: Planogram;
  branches: { id: string; name: string; color?: string | null }[];
  onClose: () => void;
}) {
  const mutations = usePlanogramMutations();
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const toggle = (id: string) =>
    setChosen((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );

  return (
    <Drawer
      title={`העתקת ${unit.name}`}
      subtitle="אל סניפים נוספים, עם כל המדפים והמוצרים"
      onClose={onClose}
      footer={(close) => (
        <div className="flex flex-wrap gap-2">
          <Button
            variant="primary"
            disabled={chosen.length === 0 || mutations.copyUnit.isPending}
            onClick={() => {
              setError(null);
              void mutations.copyUnit
                .mutateAsync({ id: unit.id, branchIds: chosen })
                .then((count) => {
                  setDone(count);
                  setChosen([]);
                })
                .catch((caught: unknown) => setError(errorMessage(caught)));
            }}
          >
            {chosen.length === 0
              ? 'העתקה לסניפים שייבחרו'
              : chosen.length === 1
                ? 'העתקה לסניף אחד'
                : `העתקה ל${chosen.length} סניפים`}
          </Button>
          <Button variant="secondary" onClick={close}>
            סגירה
          </Button>
        </div>
      )}
    >
      <div className="grid gap-3">
        <fieldset className="m-0 grid gap-2 border-0 p-0">
          <legend className="p-0 text-label font-medium">
            לאילו סניפים <span className="font-normal text-muted">אפשר לבחור כמה</span>
          </legend>

          {/*
            "בחירת הכל" קיים כי זה המקרה הנפוץ: יחידה חדשה נבנית
            פעם אחת ואמורה לרדת לכל הרשת. בלעדיו זו לחיצה לכל סניף.
          */}
          {branches.length > 1 && (
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setChosen(branches.map((branch) => branch.id))}
                disabled={chosen.length === branches.length}
                className="min-h-11 cursor-pointer rounded-sm border border-muted bg-transparent px-3 text-body-sm text-ink hover:bg-cream disabled:cursor-default disabled:text-muted"
              >
                בחירת כל הסניפים ({branches.length})
              </button>
              <button
                type="button"
                onClick={() => setChosen([])}
                disabled={chosen.length === 0}
                className="min-h-11 cursor-pointer rounded-sm border border-muted bg-transparent px-3 text-body-sm text-ink hover:bg-cream disabled:cursor-default disabled:text-muted"
              >
                ניקוי הבחירה
              </button>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {branches.map((branch) => (
              <button
                key={branch.id}
                type="button"
                aria-pressed={chosen.includes(branch.id)}
                onClick={() => toggle(branch.id)}
                className={cn(
                  'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border px-3 text-body-sm transition-colors',
                  chosen.includes(branch.id)
                    ? 'border-ink font-semibold text-ink'
                    : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                  chosen.includes(branch.id) && !branch.color && 'bg-olive',
                )}
                style={
                  chosen.includes(branch.id) && branch.color
                    ? { backgroundColor: branch.color }
                    : undefined
                }
              >
                {chosen.includes(branch.id) && (
                  <Check size={16} strokeWidth={2.5} aria-hidden="true" className="shrink-0" />
                )}
                {branch.name}
              </button>
            ))}
          </div>
        </fieldset>

        <p className="m-0 text-body-sm text-muted">
          כל סניף מקבל עותק עצמאי. שינוי שתעשה/י אחר כך בסניף אחד לא יעבור לשאר, וסניף שכבר יש בו
          יחידה באותו שם ידולג.
        </p>

        {done !== null && (
          <p role="status" className="m-0 flex items-center gap-1.5 text-body-sm text-good">
            <Check size={16} strokeWidth={2.5} aria-hidden="true" />
            {done === 0
              ? 'לא נוצר עותק חדש. בסניפים שנבחרו כבר יש יחידה באותו שם.'
              : done === 1
                ? 'העותק נוצר בסניף אחד.'
                : `נוצרו עותקים ב${done} סניפים.`}
          </p>
        )}

        {error && (
          <p role="alert" className="m-0 text-body-sm text-bad">
            {error}
          </p>
        )}
      </div>
    </Drawer>
  );
}

/**
 * מדף אחד.
 *
 * המוצרים עומדים על הלוח (items-end) ולא ממורכזים בתוך תיבה: זה
 * מה שהופך את האיור למדף במקום לשורה. הרוחב הוא היחס בין ה
 * facings של המוצר לסך המדף, ולכן התמונה מראה כמה מקום כל מוצר
 * באמת תופס.
 *
 * כלי העריכה שקטים בכוונה. כשהם היו כפתורים ממוסגרים הם משכו את
 * העין יותר מהמוצרים, וזה הפוך ממה שהמסך הזה אמור לעשות.
 */
function Shelf({
  shelf,
  index,
  run,
}: {
  shelf: PlanogramShelf;
  index: number;
  run: (action: Promise<unknown>) => void;
}) {
  const mutations = usePlanogramMutations();
  const { setNodeRef, isOver } = useDroppable({ id: `shelf:${shelf.id}` });
  const [picking, setPicking] = useState(false);

  const label = shelf.label ?? `מדף ${index + 1}`;

  return (
    <div className="grid gap-1">
      <div className="flex items-center gap-1 px-1">
        <span className="text-label font-medium text-muted">{label}</span>
        <span className="grow" />
        <button
          type="button"
          aria-label={`הוספת מוצר ל${label}`}
          aria-expanded={picking}
          onClick={() => setPicking((current) => !current)}
          className="grid size-9 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-surface hover:text-ink"
        >
          <Plus size={16} strokeWidth={2} aria-hidden="true" />
        </button>
        <button
          type="button"
          aria-label={`מחיקת ${label}`}
          onClick={() => run(mutations.removeShelf.mutateAsync(shelf.id))}
          className="grid size-9 cursor-pointer place-items-center rounded-sm bg-transparent text-muted hover:bg-surface hover:text-bad"
        >
          <Trash2 size={14} strokeWidth={2} aria-hidden="true" />
        </button>
      </div>

      {/* חיפוש ולא רשימה: אחרי קומקס יש בקטלוג אלפי מוצרים,
          ו select שמחזיק את כולם גם איטי לגלילה וגם מוריד את
          הקטלוג כולו לדפדפן */}
      {picking && (
        <div className="max-w-sm justify-self-start">
          <ProductPicker
            label={`מוצר להוספה ל${label}`}
            onPick={(product) =>
              run(
                mutations.addSlot
                  .mutateAsync({
                    shelf_id: shelf.id,
                    product_id: product.id,
                    sort_order: shelf.slots.length,
                  })
                  .then(() => setPicking(false)),
              )
            }
          />
        </div>
      )}

      {/* הלוח: הקו התחתון העבה הוא המדף, והמוצרים עומדים עליו */}
      <div
        ref={setNodeRef}
        className={cn(
          'flex min-h-32 items-end gap-1 rounded-t-sm border-b-4 border-charcoal bg-surface px-1 transition-colors',
          isOver && 'bg-olive-soft',
        )}
      >
        {shelf.slots.length === 0 ? (
          <span className="my-2 grid w-full place-items-center rounded-sm border border-dashed border-line py-6 text-label text-muted">
            מדף ריק. אפשר לגרור לכאן מוצר.
          </span>
        ) : (
          shelf.slots.map((slot) => <SlotBlock key={slot.id} slot={slot} run={run} />)
        )}
      </div>
    </div>
  );
}

/**
 * מוצר על המדף.
 *
 * עם תמונה, כי מי שמסדר מדף מחפש את האריזה ולא את הכיתוב, ובלי
 * התמונה המסך הזה הוא טבלה עם קו שחור מתחתיה.
 *
 * כל הכרטיס הוא ידית הגרירה. ידית קטנה בפינה היא מטרה שצריך
 * לכוון אליה, וכאן אין סיבה לכוון.
 *
 * הרוחב הוא פנים כפול מידה קבועה, ולא אחוז מהמדף. באחוזים מוצר
 * בודד היה נמתח על כל המדף, ומדף כמעט ריק היה נראה בדיוק כמו מדף
 * מלא — כלומר האיור היה מסתיר את הדבר היחיד שבאים לראות בו. כך
 * המקום הפנוי נשאר פנוי, וכשצפוף הכרטיסים מתכווצים יחד. */
const FACING_WIDTH = 88;

function SlotBlock({
  slot,
  run,
}: {
  slot: PlanogramShelf['slots'][number];
  run: (action: Promise<unknown>) => void;
}) {
  const mutations = usePlanogramMutations();
  const image = useImageUrl(slot.image_path);
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `slot:${slot.id}`,
  });

  return (
    <div
      className={cn('relative flex min-w-0 flex-col justify-end pt-2', isDragging && 'opacity-40')}
      style={{ flex: `0 1 ${slot.facings * FACING_WIDTH}px` }}
    >
      <button
        type="button"
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        aria-label={`${slot.product}, ${slot.facings} פנים. גרירה להזזה`}
        className="grid cursor-grab touch-none content-end gap-1 rounded-t-sm border border-line bg-cream p-1 text-start"
      >
        {image.data ? (
          <img
            src={image.data}
            alt=""
            loading="lazy"
            className="h-16 w-full rounded-sm object-contain"
          />
        ) : (
          <span className="grid h-16 place-items-center rounded-sm bg-olive-soft text-muted">
            <Package size={20} strokeWidth={1.75} aria-hidden="true" />
          </span>
        )}

        <span className="line-clamp-2 text-label font-medium break-words text-ink">
          {slot.product}
        </span>
      </button>

      <button
        type="button"
        aria-label={`הסרת ${slot.product} מהמדף`}
        onClick={() => run(mutations.removeSlot.mutateAsync(slot.id))}
        className="absolute top-1 end-1 grid size-6 cursor-pointer place-items-center rounded-sm border border-line bg-surface text-muted hover:text-bad"
      >
        <X size={12} strokeWidth={2} aria-hidden="true" />
      </button>

      <span className="flex items-center justify-center rounded-b-sm border border-t-0 border-line bg-surface px-1 py-0.5">
        <span className="flex items-center gap-0.5">
          <button
            type="button"
            aria-label={`פחות פנים ל${slot.product}`}
            disabled={slot.facings <= 1}
            onClick={() =>
              run(mutations.setFacings.mutateAsync({ id: slot.id, facings: slot.facings - 1 }))
            }
            className="grid size-6 cursor-pointer place-items-center rounded-sm bg-transparent text-muted enabled:hover:bg-cream enabled:hover:text-ink disabled:opacity-40"
          >
            <Minus size={12} strokeWidth={2} aria-hidden="true" />
          </button>
          <span className="tabular text-label text-muted">{slot.facings}</span>
          <button
            type="button"
            aria-label={`עוד פנים ל${slot.product}`}
            disabled={slot.facings >= 20}
            onClick={() =>
              run(mutations.setFacings.mutateAsync({ id: slot.id, facings: slot.facings + 1 }))
            }
            className="grid size-6 cursor-pointer place-items-center rounded-sm bg-transparent text-muted enabled:hover:bg-cream enabled:hover:text-ink disabled:opacity-40"
          >
            <Plus size={12} strokeWidth={2} aria-hidden="true" />
          </button>
        </span>
      </span>
    </div>
  );
}
